// Session scope = the legal entity + operation unit the user works in, optionally narrowed
// to one department (mandatory for members of a fundraising department). It must be picked
// after every sign-in (it is bound to the JWT it was chosen under, so a new login — i.e. a
// new token — always asks again) and survives page reloads within that login.
// The backend never trusts it: every call re-validates the pair against the user's access.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { useAuth } from "@/lib/auth";
import type { SessionScope } from "@/lib/donation";

// v2: department added (older saved scopes lack it, so everyone picks once more).
const STORAGE_KEY = "icm_donation_scope_v2";

interface StoredScope extends SessionScope {
  token: string;
}

interface ScopeState {
  scope: SessionScope | null;
  setScope: (scope: SessionScope) => void;
  clearScope: () => void;
}

const ScopeContext = createContext<ScopeState | null>(null);

function readStored(): StoredScope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredScope) : null;
  } catch {
    return null;
  }
}

export function SessionScopeProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  // In-memory copy; localStorage only carries it across reloads.
  const [stored, setStored] = useState<StoredScope | null>(readStored);

  // Valid only for the login (token) it was chosen under.
  const scope = useMemo<SessionScope | null>(
    () =>
      stored && token && stored.token === token
        ? {
            legalEntity: stored.legalEntity,
            operationUnit: stored.operationUnit,
            department: stored.department ?? null,
            departmentLocked: stored.departmentLocked ?? false,
          }
        : null,
    [stored, token]
  );

  const setScope = useCallback(
    (next: SessionScope) => {
      if (!token) return;
      const value: StoredScope = { ...next, token };
      setStored(value);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch {
        // storage unavailable — the scope still applies until reload
      }
    },
    [token]
  );

  const clearScope = useCallback(() => {
    setStored(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return (
    <ScopeContext.Provider value={{ scope, setScope, clearScope }}>{children}</ScopeContext.Provider>
  );
}

export function useSessionScope() {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useSessionScope must be used within SessionScopeProvider");
  return ctx;
}
